import { Field } from '@base-ui/react/field';
import { Fieldset } from '@base-ui/react/fieldset';
import { NumberField } from '@/components/ui/NumberField';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Select } from '@/components/ui/Select';
import { Slider } from '@/components/ui/Slider';
import {
  SCORE_TICKS,
  SCORING_MODELS,
  TAILORING_MODELS,
} from '@/consts/settings';
import { cx } from '@/cva.config';
import { useScoringSettings } from '@/providers/settings/SettingsContext';

export const ScoringSection = () => {
  const {
    mark,
    maxScore,
    maxTailor,
    scoringModel,
    setMaxScore,
    setMaxTailor,
    setScoringModel,
    setTailoringModel,
    setThreshold,
    tailoringModel,
    threshold,
  } = useScoringSettings();

  return (
    <>
      {/* Scoring */}
      <Fieldset.Root>
        <Fieldset.Legend render={<SectionHeader>Scoring</SectionHeader>} />
        <div className="space-y-4">
          <Field.Root className="block">
            <div className="mb-1.5 flex items-baseline justify-between">
              <Field.Label className="text-sm lg:text-base font-medium text-foreground">
                Minimum match score for tailoring
              </Field.Label>
            </div>
            <div className="rounded-md border border-border bg-surface/60 p-4">
              <div className="relative pt-5">
                <div
                  className="absolute top-0 -translate-x-1/2 font-mono text-xs lg:text-base text-accent tabular-nums"
                  style={{ left: `${threshold}%` }}
                >
                  {threshold}
                </div>
                <Slider
                  max={100}
                  min={0}
                  onChange={mark(setThreshold)}
                  step={5}
                  value={threshold}
                />
              </div>
              <div className="relative mt-2 h-8 font-mono text-xs lg:text-sm">
                {SCORE_TICKS.map((tick) => (
                  <div
                    className={cx(
                      'absolute',
                      tick.align === 'left' && 'text-left',
                      tick.align === 'center' && '-translate-x-1/2 text-center',
                      tick.align === 'right' && '-translate-x-full text-right',
                    )}
                    key={tick.value}
                    style={{ left: `${tick.value}%` }}
                  >
                    <div className="text-foreground tabular-nums">
                      {tick.value}
                    </div>
                    <div className="text-subtle">{tick.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </Field.Root>
          <div className="grid grid-cols-2 gap-3">
            <Field.Root className="flex flex-col gap-1.5">
              <div className="flex items-baseline">
                <Field.Label className="text-sm lg:text-base font-medium text-foreground">
                  Scoring model
                </Field.Label>
              </div>
              <Select
                items={SCORING_MODELS}
                onValueChange={mark(setScoringModel)}
                value={scoringModel}
              />
            </Field.Root>
            <Field.Root className="flex flex-col gap-1.5">
              <div className="flex items-baseline">
                <Field.Label className="text-sm lg:text-base font-medium text-foreground">
                  Tailoring model
                </Field.Label>
              </div>
              <Select
                items={TAILORING_MODELS}
                onValueChange={mark(setTailoringModel)}
                value={tailoringModel}
              />
            </Field.Root>
            <Field.Root className="flex flex-col gap-1.5">
              <div className="flex items-baseline justify-between">
                <Field.Label className="text-sm lg:text-base font-medium text-foreground">
                  Max score per cycle
                </Field.Label>
                <Field.Description className="font-mono text-xs lg:text-sm text-subtle">
                  default 100
                </Field.Description>
              </div>
              <NumberField
                font="mono"
                min={0}
                onValueChange={(v) => mark(setMaxScore)(v ?? 0)}
                size="sm"
                step={1}
                value={maxScore}
              />
            </Field.Root>
            <Field.Root className="flex flex-col gap-1.5">
              <div className="flex items-baseline justify-between">
                <Field.Label className="text-sm lg:text-base font-medium text-foreground">
                  Max tailor per cycle
                </Field.Label>
                <Field.Description className="font-mono text-xs lg:text-sm text-subtle">
                  default 50
                </Field.Description>
              </div>
              <NumberField
                font="mono"
                min={0}
                onValueChange={(v) => mark(setMaxTailor)(v ?? 0)}
                size="sm"
                step={1}
                value={maxTailor}
              />
            </Field.Root>
          </div>
        </div>
      </Fieldset.Root>
    </>
  );
};
